import {
  ViewFolderId,
  ViewId,
} from "@redotech/effect-api-shared/ids/definitions";
import { useHandler } from "@redotech/react-util/hook";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import { useSupportViewsFoldersCollections } from "../providers/support-views-folders-provider";

// Historical persisted view/folder table id. New support-v2 code should use
// support ticket terminology around this value, but changing the value itself
// requires a view/folder migration.
const LEGACY_SUPPORT_TICKET_VIEW_TABLE = "support-v2-conversations" as const;

interface ViewFolderInfo {
  id: string;
  folderId: string | null;
}

export function useSupportV2FolderActions(): {
  createFolder: (
    name: string,
    viewIds: string[],
    isPrivate: boolean,
  ) => Promise<void>;
  updateFolder: (
    folderId: string,
    name: string,
    viewIds: string[],
    allViews: ViewFolderInfo[],
  ) => Promise<void>;
  deleteFolder: (folderId: string) => Promise<void>;
  createView: (
    name: string,
    filterGroup: FilterGroup | null,
    folderId: string | null,
  ) => Promise<string>;
  updateView: (
    viewId: string,
    name: string,
    filterGroup: FilterGroup | null,
  ) => Promise<void>;
  deleteView: (viewId: string) => Promise<void>;
} {
  const collections = useSupportViewsFoldersCollections();

  const createView = useHandler(
    async (
      name: string,
      filterGroup: FilterGroup | null,
      folderId: string | null,
    ): Promise<string> => {
      const viewId = ViewId.generate();
      collections.views.insert({
        id: viewId,
        name,
        table: LEGACY_SUPPORT_TICKET_VIEW_TABLE,
        filters: [],
        filterGroup,
        columns: [],
        sort: null,
        pageSize: null,
        folderId: folderId ? ViewFolderId.make(folderId) : null,
        position: 0,
        deleted: false,
        isNotEditable: false,
      });
      return String(viewId);
    },
  );

  const updateView = useHandler(
    async (
      viewId: string,
      name: string,
      filterGroup: FilterGroup | null,
    ): Promise<void> => {
      collections.views.update(ViewId.make(viewId), (draft) => {
        draft.name = name;
        draft.filterGroup = filterGroup;
      });
    },
  );

  const deleteView = useHandler(async (viewId: string): Promise<void> => {
    collections.views.delete(ViewId.make(viewId));
  });

  const createFolder = useHandler(
    async (name: string, viewIds: string[], isPrivate: boolean) => {
      const folderId = ViewFolderId.generate();
      collections.folders.insert({
        id: folderId,
        name,
        table: LEGACY_SUPPORT_TICKET_VIEW_TABLE,
        position: 0,
        isPrivate,
      });
      // Assign views to the new folder
      for (const viewId of viewIds) {
        collections.views.update(ViewId.make(viewId), (draft) => {
          draft.folderId = folderId;
        });
      }
    },
  );

  const updateFolder = useHandler(
    async (
      folderId: string,
      name: string,
      viewIds: string[],
      allViews: ViewFolderInfo[],
    ) => {
      const folderIdTyped = ViewFolderId.make(folderId);
      collections.folders.update(folderIdTyped, (draft) => {
        draft.name = name;
      });
      // Update view assignments: add folderId to selected views, remove from unselected
      const selectedSet = new Set(viewIds);
      for (const view of allViews) {
        const isSelected = selectedSet.has(view.id);
        const currentlyInThisFolder = view.folderId === folderId;

        if (isSelected && !currentlyInThisFolder) {
          // Add to this folder
          collections.views.update(ViewId.make(view.id), (draft) => {
            draft.folderId = folderIdTyped;
          });
        } else if (!isSelected && currentlyInThisFolder) {
          // Remove from this folder
          collections.views.update(ViewId.make(view.id), (draft) => {
            draft.folderId = null;
          });
        }
      }
    },
  );

  const deleteFolder = useHandler(async (folderId: string) => {
    collections.folders.delete(ViewFolderId.make(folderId));
  });

  return {
    createView,
    updateView,
    deleteView,
    createFolder,
    updateFolder,
    deleteFolder,
  };
}
